import { Loader2, MapPin } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { api } from "../lib/api";

const token = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Address field with Google Maps suggestions; picking one fills street, city and state. */
export function AddressField({ value, onChange, onSelected }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [focused, setFocused] = useState(false);
  const session = useRef(token());
  const skip = useRef(true);
  useEffect(() => {
    if (skip.current) {
      skip.current = false;
      return undefined;
    }
    const q = (value ?? "").trim();
    if (q.length < 3 || !focused) {
      setItems([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await api("/places/autocomplete", { method: "POST", body: { input: q, sessionToken: session.current } });
        if (!cancelled) setItems(res.suggestions ?? []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [value, focused]);
  const pick = async (s) => {
    setItems([]);
    skip.current = true;
    onChange(s.description);
    try {
      const d = await api("/places/details", { method: "POST", body: { placeId: s.placeId, sessionToken: session.current } });
      skip.current = true;
      onSelected(d);
    } catch {
      /* keep the typed description */
    }
    session.current = token();
  };
  return (
    <div className="relative mb-4">
      <span className="mb-1.5 block text-[13px] font-semibold text-wf-ink2">Address (Google Maps autofill)</span>
      <div className="flex h-12 items-center gap-2 rounded-xl border border-wf-line bg-wf-s2 px-4 focus-within:border-wf-brand focus-within:bg-wf-surface">
        <MapPin size={18} className="text-wf-brand" />
        <input data-testid="customer-address-input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 250)} placeholder="Start typing an address" autoComplete="off" className="h-full flex-1 bg-transparent text-[15px] outline-none" />
        {busy && <Loader2 size={16} className="animate-spin text-wf-brand" />}
      </div>
      {items.length > 0 && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-wf-line bg-wf-surface shadow-xl" data-testid="address-suggestions">
          {items.map((s, i) => (
            <button key={s.placeId} type="button" data-testid={`address-suggestion-${i}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(s)}
              className="flex min-h-[44px] w-full items-center gap-2 border-b border-wf-line px-4 text-left text-sm hover:bg-wf-s3">
              <MapPin size={14} className="shrink-0 text-wf-mute" />{s.description}
            </button>
          ))}
          <p className="p-1.5 text-right text-[11px] text-wf-mute">Powered by Google</p>
        </div>
      )}
    </div>
  );
}
